import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import * as XLSX from 'xlsx';
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import { setSnackBarData } from '../../../services/slices/snackBarSlice';
import AlertMessage from '../bulk-import/child-components/AlertMessages';
import { isValue } from '../../../utils/constants/constants';
import { clearAllReduxStates } from '../../../utils/common-hooks/clearState';
import { persistor } from '../../../store/store';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { setLogoutSession } from '../../../services/slices/authTokenSlice';
import { responseData } from '../../../utils/helpers/helper';
import { NO_RESPONSE_MESSAGE } from '../../../utils/common-hooks/fetchData';

const BulkDownload = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const [disableDownload, setDisableDownload] = useState(false);
    const { fullName } = useSelector((state) => state.authToken.authTokenResponseData);

    const { fileName, type, errorData, onDownloadComplete, setIsLoading, setAlertMessagePropss } = props;
    const [alertMessageProps, setAlertMessageProps] = useState({
        open: false, onClose: () => { }, header: isValue?.emptyString, body: () => { }, footer: () => { }
    })
    const totalShopsCount = useSelector(state => state?.MyMarket?.mymarket?.totalCount)

    useEffect(() => {
        let rateShopDownloadStatus = JSON.parse((sessionStorage.getItem("isRateShopDownloading"))) || false
        const disablebtn = type === "rateshop" ? rateShopDownloadStatus : false;
        setDisableDownload(disablebtn)
    }, [type])

    const generateExcel = async () => {
        setIsLoading && setIsLoading(true);
        setAlertMessagePropss && setAlertMessagePropss((prev) => { return { ...prev, ...{ open: false } } })
        setDisableDownload(true);
        let data = []
        if (type === "rateshop") {
            sessionStorage.setItem("isRateShopDownloading", JSON.stringify(true))

            try {
                const response = await axios.get(process.env.REACT_APP_API_URL + 'mymarketsexcel', {
                    responseType: 'blob', // Set the response type to blob
                });
                if (response?.response?.status === 401 && responseData(response?.response)?.message === "New login found.") {
                    dispatch(setLogoutSession(true));
                }

                // Create a Blob from the response data
                const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                // Create a link element
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);

                // Set the download attribute with the desired file name
                link.download = `My Market Shops_${fullName.replace(/\s+/g, "")}_${moment().format('YYYYMMDDTHHmm')}.xlsx`;

                // Append the link to the document
                document.body.appendChild(link);

                // Trigger a click on the link to start the download
                link.click();

                // Remove the link from the document
                document.body.removeChild(link);
                setDisableDownload(false)
                dispatch(setSnackBarData("File Download Completed!"))
                onDownloadComplete && onDownloadComplete(); // to close the modal when error report downloaded.
                JSON.stringify(sessionStorage.setItem("isRateShopDownloading", false))
                return
            } catch (error) {
                console.log("API failed", error?.response?.status, error?.message)
                // With responseType 'blob' the error body is a Blob unless the secure channel already parsed it
                let body = error?.response?.data;
                if (typeof Blob !== "undefined" && body instanceof Blob) {
                    try {
                        body = JSON.parse(await body.text());
                    } catch (parseError) {
                        body = null;
                    }
                }
                const message = responseData(error?.response && { ...error.response, data: body })?.message;
                if (error?.response?.status === 401 && message === "New login found.") {
                    dispatch(setLogoutSession(true));
                } else if (error?.response?.status === 401) {
                    clearAllReduxStates(dispatch)
                    persistor.purge();
                    sessionStorage.clear();
                    localStorage.clear();
                    navigate("/")
                } else {
                    dispatch(setSnackBarData(message || (error?.response ? "The download failed. Please try again." : NO_RESPONSE_MESSAGE)))
                }
                // Stop here: falling through would build and download an empty workbook
                setDisableDownload(false)
                sessionStorage.setItem("isRateShopDownloading", JSON.stringify(false))
                setIsLoading && setIsLoading(false);
                return
            }
        } 
        else if (type === "error") {
            data = errorData
        }



        // Extract headers and rows from the data
        const headers = [...new Set(data?.flatMap(Object.keys))]
        const rows = data?.map((obj) => Object.values(obj));

        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

        // Calculate column widths for headers and data cells
        const columnWidths = headers?.map((header) => header.length + 2); // Adjust the +2 to your preference
        const dataColumnWidths = rows[0]?.map((_, colIdx) =>
            Math.max(
                columnWidths[colIdx], // Use the widths calculated for headers
                ...rows?.map((row) => (row[colIdx] ? row[colIdx]?.toString().length + 1 : 0))
            )
        );

        // Apply column widths for both headers and data cells
        sheet['!cols'] = columnWidths?.map((width) => ({ wch: width }));

        // Modify the width of the "Error Message" column to 30 characters
        const errorMessageColumnWidth = 100;
        const errorMessageHeaderIndex = headers?.findIndex((header) => header === 'Error Message');

        if (errorMessageHeaderIndex !== -1) {
            sheet['!cols'][errorMessageHeaderIndex].wch = errorMessageColumnWidth;
        }

        for (let colIdx = 0; colIdx < dataColumnWidths?.length; colIdx++) {
            if (colIdx !== errorMessageHeaderIndex) {
                sheet['!cols'][colIdx].wch = dataColumnWidths[colIdx];
            }
        }


        // Calculate row heights based on content length for data cells
        const dataRowHeights = rows?.map((row) =>
            Math.max(...row?.map((cell) => (cell ? cell?.toString()?.split('\n')?.length : 1))) * 20 // Adjust the multiplier as needed for data rows
        );

        // Calculate row height for header cells (double the height of data rows)
        const headerRowHeight = 30; // Adjust the default header row height as needed

        // Apply row height for header cells
        sheet['!rows'] = sheet['!rows'] || [];
        sheet['!rows'][0] = { hpt: headerRowHeight };

        // Apply row heights for data cells
        dataRowHeights?.forEach((height, rowIdx) => {
            sheet['!rows'][rowIdx + 1] = { hpt: height };
        });

        workbook.SheetNames.push('Sheet1');
        workbook.Sheets['Sheet1'] = sheet;
        const excelBlob = await workBookToBlob(workbook);

        // Function to convert column index to Excel column letter
        const getColumnLetter = (index) => {
            let letter = isValue?.emptyString;
            while (index >= 0) {
                letter = String.fromCharCode(65 + (index % 26)) + letter;
                index = Math.floor(index / 26) - 1;
            }
            return letter;
        }

        // Apply styles using XlsxPopulate
        const numColumns = headers.length; // Get the number of columns dynamically
        const headerStart = 'A1';
        const headerEnd = getColumnLetter(numColumns - 1) + '1'; // Calculate the end column dynamically
        const dataStart = 'A2';
        const dataEnd = getColumnLetter(numColumns - 1) + (rows.length + 1); // Calculate the end row dynamically

        // Find the index of the "Error Message" header in the headers array
        let errorStart = 'AB2'; // Default value if the "Error Message" header is not found
        let errorEnd = 'AB2';   // Default value if the "Error Message" header is not found

        if (errorMessageHeaderIndex !== -1) {
            // Calculate the range for the "Error Message" column based on its index
            errorStart = getColumnLetter(errorMessageHeaderIndex) + '2';
            errorEnd = getColumnLetter(errorMessageHeaderIndex) + (rows.length + 1);
        }

        const dataInfo = {
            headerRange: `${headerStart}:${headerEnd}`, // Range of headers
            dataRange: `${dataStart}:${dataEnd}`, // Range of data, +1 to account for header row
            ...(errorData && errorData.length > 0 && { errorRange: `${errorStart}:${errorEnd}` }),
        };

        // Create a download URL for the styled Excel file
        const url = type !== "rateshop" && (((parseInt(totalShopsCount) > 10000) && ((type === "error"))) ? URL.createObjectURL(excelBlob) : URL.createObjectURL(await addStyle(excelBlob, dataInfo)));

        // Download the file
        if (fileName === "My Market Shops.xlsx") {
            setDisableDownload(false)
            dispatch(setSnackBarData("File Download Completed!"))
            JSON.stringify(sessionStorage.setItem("isRateShopDownloading", false))
        }
        else {
            setDisableDownload(false)
        }
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute('href', url);
        downloadAnchorNode.setAttribute('download', (fileName === "Bulk_Error_Report.xlsx" ? `Bulk_Error_Report_${fullName.replace(/\s+/g, "")}_${moment().format('YYYYMMDDTHHmm')}.xlsx` : fileName || 'shops.xlsx'));
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
        onDownloadComplete && onDownloadComplete(); // to close the modal when error report downloaded.
    };

    const workBookToBlob = (workbook) => {
        const wopts = {
            bookType: 'xlsx',
            bookSST: false,
            type: 'binary', // Change to 'blob' to create a Blob directly
        };
        const wbout = XLSX.write(workbook, wopts);
        return new Blob([s2ab(wbout)], {
            type: 'application/octet-stream',
        });
    };

    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i !== s.length; ++i) {
            view[i] = s.charCodeAt(i) & 0xFF;
        }
        return buf;
    };

    const addStyle = async (workbookBlob, dataInfo) => {
        const workbook = await XlsxPopulate.fromDataAsync(workbookBlob);
        workbook.sheets().forEach((sheet) => {
            sheet.range(dataInfo?.headerRange).style({
                fill: '07748F',
                bold: true,
                border: true,
                fontColor: 'ffffff',
                fontFamily: "Arial",
                fontSize: 12,
                verticalAlignment: 'center',
                horizontalAlignment: 'center',
            })

            sheet.range(dataInfo?.dataRange).style({
                border: true,
                fontFamily: "Arial",
                verticalAlignment: 'center',
                horizontalAlignment: 'center',
            });

            props.errorData && props.errorData.length > 0 && sheet.range(dataInfo?.errorRange).style({
                fontColor: 'FF0000',
                wrapText: true, // Enable text wrapping
            });

        });

        // Convert the workbook to a Blob
        const styledBlob = await workbook.outputAsync('blob');
        return styledBlob;
    };

    const handleDetailspopupclose = () => {
        setAlertMessageProps((prev) => { return { ...prev, ...{ open: false, header: isValue?.emptyString, body: isValue?.emptyString } } })
    }

    const generateCommonModalTitle = (tilte) => {
        return (
            <div className="d-flex justyfy-space-between title-modal">
                <h5>{tilte}</h5>
                <h3 onClick={() => handleDetailspopupclose()} className="cursor-p">+</h3>
            </div>
        )
    }

    const downloadWarningExcel = async () => {
        setIsLoading && setIsLoading(true);
        setAlertMessagePropss && setAlertMessagePropss((prev) => { return { ...prev, ...{ open: false } } });
        setAlertMessageProps(
            {
                open: true,
                onClose: () => setAlertMessageProps((prev) => { return { ...prev, ...{ open: false } } }),
                header: generateCommonModalTitle("Download Inprogress"),
                body: <p>Please wait, the previous download is still in progress.</p>,
                footer: <button onClick={() => setAlertMessageProps((prev) => { return { ...prev, ...{ open: false } } })}>OK</button>
            })
    };

    return (
        <>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a title="Download" onClick={disableDownload ? downloadWarningExcel : generateExcel} className={props.type === 'error' ? "btn-donload-reports" : ""} >
                {props.type === 'error' ? "Download Report" : disableDownload ? "Downloading..." : "Download"}
            </a>
            <AlertMessage {...alertMessageProps} />
        </>
    );
}

export default BulkDownload;